import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api";
import { useNotification } from "~hooks/useNotification";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";

export const useFetchLeague = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, dataId } = useParams();

  return useQuery({
    queryKey: ["leagues", sport, organizationId, dataId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/leagues/${dataId}`,
      });
      return result?.data?.[0] || [];
    },
    enabled: !!dataId,
  });
};

export const useFetchLeagues = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/leagues`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["leagues", sport, organizationId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useCreateLeague = () => {
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        method: "POST",
        url: `${sport}/o/${organizationId}/leagues`,
        data,
      });

      return result?.data?.[0];
    },
    onSuccess: async (createdLeague) => {
      if (!createdLeague?.leagueId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources.leagues) {
          updatedScope.resources.leagues[createdLeague.leagueId] = createdLeague;
        } else {
          updatedScope.resources.leagues = {
            [createdLeague.leagueId]: createdLeague,
          };
        }

        updatedScope.scope.leagueTree.push({
          leagueId: createdLeague.leagueId,
          competitions: [],
        });
      });

      notification.success({
        message: formatMessage({ id: "league.create.success" }),
        placement: "bottomRight",
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["leagues", sport, organizationId] })]);
    },
  });
};

export const useUpdateLeague = () => {
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const { sport, organizationId, dataId } = useParams();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        method: "PUT",
        url: `${sport}/o/${organizationId}/leagues/${dataId}`,
        data,
      });

      return result?.data?.[0];
    },
    onSuccess: async (updatedLeague) => {
      if (!updatedLeague?.leagueId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        const existingLeague = updatedScope.resources?.leagues?.[dataId];

        if (existingLeague && updatedScope.resources.leagues) {
          updatedScope.resources.leagues[dataId] = {
            ...existingLeague,
            ...updatedLeague,
            leagueId: dataId,
          };
        }
      });

      notification.success({
        message: `${formatMessage({ id: "league.update.success" })}`,
        placement: "bottomRight",
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["leagues", sport, organizationId] })]);
    },
  });
};
